import { ChangeEvent, RefObject, useEffect } from "react";
import Button from "../buttons/button/Button";
import { BUTTON_TYPES } from "../buttons/button/ButtonTypes";
import {
  ProgressIndicator,
  SIZE_SMALL,
} from "@asosteam/asos-web-component-library-progress-indicator";
import styles from "./AnonymousBackInStockModal.scss";
import validateEmail from "./utils/validateEmail";
import replaceJSX from "./utils/replaceJSX";
import { fireEnterEmailScreenLoadAnalytics } from "../../analytics/anonymousBackInStock";

type Props = {
  emailInputRef: RefObject<HTMLInputElement>;
  email: string;
  emailError: boolean;
  twoStep: boolean;
  loading: boolean;
  setEmail: (email: string) => void;
  setEmailError: (error: boolean) => void;
  handleNotifyClick: () => void;
  getTranslation: (key: string) => string;
  title?: string;
  body?: string;
  buttonType?: string;
  subLabel?: string;
  selectedVariantId?: number;
};

const AnonymousBackInStockNotRecognisedCustomer = ({
  emailInputRef,
  email,
  emailError,
  loading,
  setEmail,
  setEmailError,
  handleNotifyClick,
  getTranslation,
  title,
  body,
  buttonType = BUTTON_TYPES.primary,
  subLabel,
  selectedVariantId,
  twoStep,
}: Props): JSX.Element => {
  const resolvedTitle =
    title !== undefined
      ? title
      : getTranslation("anonymous_back_in_stock_modal_heading");

  useEffect(() => {
    if (twoStep) {
      fireEnterEmailScreenLoadAnalytics(selectedVariantId);
    }
  }, [selectedVariantId, twoStep]);
  const resolvedBody =
    body !== undefined
      ? body
      : `${getTranslation(
          "back_in_stock_sign_in_modal_title",
        )}. ${getTranslation("back_in_stock_sign_in_modal_text")}`;
  const notifyMeButtonCopy = getTranslation(
    "anonymous_back_in_stock_modal_cta",
  );

  return (
    <>
      {resolvedTitle && (
        <h2 id="modalTitle" className={styles.title}>
          {resolvedTitle}
        </h2>
      )}
      {resolvedBody && <p className={styles.intro}>{resolvedBody}</p>}
      <div className={styles.inputWrapper}>
        {subLabel && (
          <p
            className={`${styles.intro} ${styles.introWithBottomPadding} ${styles.introSubLabel}`}
          >
            {subLabel}
          </p>
        )}
        <input
          ref={emailInputRef}
          name="email"
          type="email"
          className={styles.emailInput}
          placeholder={getTranslation(
            "anonymous_back_in_stock_modal_email_form",
          )}
          aria-label={getTranslation(
            "anonymous_back_in_stock_modal_email_form",
          )}
          aria-invalid={emailError}
          aria-describedby={emailError ? "anon-bis-email-error" : undefined}
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          onBlur={() => email && setEmailError(!validateEmail(email))}
        />
      </div>
      {emailError && (
        <p
          id="anon-bis-email-error"
          className={styles.emailValidationError}
          role="alert"
        >
          {getTranslation("anonymous_back_in_stock_modal_email_error")}
        </p>
      )}
      <Button
        handleClick={handleNotifyClick}
        type={buttonType}
        additionalClasses={styles.button}
        disabled={loading}
        ariaLabel={notifyMeButtonCopy}
      >
        {loading ? (
          <ProgressIndicator size={SIZE_SMALL} aria-hidden="true" />
        ) : (
          notifyMeButtonCopy
        )}
      </Button>
      <div className={styles.consentWarning} data-testid="anon-bis-policy">
        {replaceJSX(
          getTranslation("anonymous_back_in_stock_modal_terms_link"),
          ["%{privacypolicylink}", "%{termslink}"],
          [
            <a
              key="privacypolicylink"
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              {getTranslation("anonymous_back_in_stock_modal_policy_link_text")}
            </a>,
            <a
              key="termslink"
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              {getTranslation("anonymous_back_in_stock_modal_terms_link_text")}
            </a>,
          ],
        )}
      </div>
    </>
  );
};

export default AnonymousBackInStockNotRecognisedCustomer;
